import { QueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { dispatchSupporterReturnOnce } from "@/app/supporterReturnDispatch";
import { shouldDeferCatalogLayout } from "@/features/catalog/utils/catalogLayoutRoute";
import { usePlayerStore } from "@/features/player/stores/playerStore";
import { useSettingsStore } from "@/features/settings/hooks";
import { resolveLightMode } from "@/features/settings/stores/legacyDisplaySettings";
import { resolvePostFontFamily } from "@/features/settings/utils/renderSettings";
import { refreshSupporterSession } from "@/features/supporter/hooks/useSupporterSession";
import {
  createSupporterReturnMediator,
  isSupporterReturnEvent,
  type SupporterReturnEffect,
  type SupporterReturnEvent,
  type SupporterReturnState,
} from "@/features/supporter/mediators/supporterReturnMediator";
import { readSupporterReturn } from "@/features/supporter/model/supporterReturn";
import { useSupporterStore } from "@/features/supporter/stores/supporterStore";
import { useIsDesktop, useSystemPrefersDark } from "@/shared/hooks";
import { useHasFinePointer } from "@/shared/hooks/useMediaQuery";
import { useOnlineUsersHeartbeat } from "@/shared/hooks/useOnlineUsersHeartbeat";
import { useRouteCanonical } from "@/shared/hooks/useRouteCanonical";
import type { Mediator } from "@/shared/lib/componentHierarchy";
import {
  getHierarchyResourceSnapshot,
  useComponentNode,
  useHierarchyResource,
} from "@/shared/ui/component-hierarchy/hooks";

type RootSupporterReturnMediator = Mediator<
  SupporterReturnState,
  SupporterReturnEvent,
  SupporterReturnEffect
>;

export interface RootPresentation {
  queryClient: QueryClient;
  privacyMode: boolean;
  miniPlayerVisible: boolean;
  jukeboxPlaybackSessionActive: boolean;
  isDesktop: boolean;
  hasFinePointer: boolean;
  pathname: string;
  isStandaloneJukebox: boolean;
  deferCatalogLayout: boolean;
}

export function useRootPresenter(): RootPresentation {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
          },
        },
      }),
  );
  useRenderSettings();

  const privacyMode = useSettingsStore((state) => state.privacyMode);
  const miniPlayerVisible = usePlayerStore((state) => state.miniPlayer.visible);
  const jukeboxPlaybackSessionActive = usePlayerStore(
    (state) => state.jukeboxPlaybackSession,
  );
  const isDesktop = useIsDesktop();
  const hasFinePointer = useHasFinePointer();
  const location = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  useRouteCanonical(location.pathname);
  useOnlineUsersHeartbeat(location.pathname !== "/jukebox");

  // standalone は小さい別ウィンドウでも通常のモバイル枠を外す。
  const isStandaloneJukebox =
    location.pathname === "/jukebox" &&
    (isDesktop ||
      (location.search as { standalone?: boolean }).standalone === true);

  const returnMediator = useHierarchyResource<RootSupporterReturnMediator>(
    (previous) =>
      createSupporterReturnMediator(
        (effect) => {
          switch (effect.type) {
            case "supporter-return/clean-url":
              router.history.replace(effect.url);
              break;
            case "supporter-return/set-login-return":
              useSupporterStore.getState().setLoginReturn(effect.status);
              break;
            case "supporter-return/refresh-session":
              void refreshSupporterSession();
              break;
            case "supporter-return/navigate-to-settings":
              void navigate({
                to: "/settings",
                search: {},
                replace: effect.replace,
              });
              break;
          }
        },
        previous?.isDisposed
          ? getHierarchyResourceSnapshot<SupporterReturnState>(previous)
          : undefined,
      ),
  );
  const returnNode = useComponentNode((event) => {
    if (!isSupporterReturnEvent(event)) return "unhandled";
    return returnMediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const hasReadReturnRef = useRef(false);

  useEffect(() => {
    if (hasReadReturnRef.current) return;
    const supporterReturn = readSupporterReturn(window.location.href);
    if (supporterReturn === null) {
      hasReadReturnRef.current = true;
      return;
    }

    const event: SupporterReturnEvent = {
      type: "supporter-return/detected",
      ...supporterReturn,
      isDesktop,
      isSettingsRoute: /(?:^|\/)settings\/?$/.test(location.pathname),
    };
    dispatchSupporterReturnOnce(
      hasReadReturnRef,
      () => returnNode.dispatch(event).status,
    );
  }, [isDesktop, location.pathname, returnNode]);

  return {
    queryClient,
    privacyMode,
    miniPlayerVisible,
    jukeboxPlaybackSessionActive,
    isDesktop,
    hasFinePointer,
    pathname: location.pathname,
    isStandaloneJukebox,
    deferCatalogLayout: shouldDeferCatalogLayout(location.pathname),
  };
}

function useRenderSettings(): void {
  const darkMode = useSettingsStore((state) => state.darkMode);
  const globalFontScale = useSettingsStore((state) => state.globalFontScale);
  const postBodyFontSize = useSettingsStore((state) => state.postBodyFontSize);
  const fontFamily = useSettingsStore((state) => state.fontFamily);
  const systemPrefersDark = useSystemPrefersDark();

  useLayoutEffect(() => {
    document.documentElement.classList.toggle(
      "light-mode",
      resolveLightMode(darkMode, systemPrefersDark),
    );
  }, [darkMode, systemPrefersDark]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "font-size",
      `${globalFontScale / 100}rem`,
    );
  }, [globalFontScale]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      "--post-body-font-size",
      `${postBodyFontSize}px`,
    );
  }, [postBodyFontSize]);

  useLayoutEffect(() => {
    const postFontFamily = resolvePostFontFamily(fontFamily);
    if (postFontFamily === null) {
      document.documentElement.style.removeProperty("--post-font-family");
      return;
    }
    document.documentElement.style.setProperty(
      "--post-font-family",
      postFontFamily,
    );
  }, [fontFamily]);
}
